import { useState, useCallback, useRef, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Tab,
  Tabs,
  Tooltip,
  LinearProgress
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SpeedIcon from "@mui/icons-material/Speed";
import DownloadIcon from "@mui/icons-material/Download";
import StopIcon from "@mui/icons-material/Stop";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TableChartIcon from "@mui/icons-material/TableChart";
import { PageWrapper } from "../../common-ui/PageWrapper";
import { API, Auth } from "aws-amplify";
import { QueryPlanExplorer, type PlanRow, type ExplainRow } from "./QueryPlanExplorer";
import { useSearchParams } from "react-router-dom";

type RegisteredQuery = {
  id: string;
  name: string;
  description: string;
  category: string;
  schema: any;
  hasResolver: boolean;
  resolverSchema: any;
};

type ProgramOption = {
  id: string;
  name: string;
  institutionName?: string;
};

type MemberOption = {
  userId: string;
  fullName: string;
  email: string;
  roleName: string;
  membershipId: string;
};

type RunResult = {
  columns: string[];
  rows: Record<string, any>[];
  timing: number;
  rowCount: number;
  hash: string;
  sql: string;
  timestamp: string;
};

type BenchmarkRun = {
  iteration: number;
  timing: number;
  rowCount: number;
  hash: string;
  error?: string;
};

type BenchmarkResult = {
  runs: BenchmarkRun[];
  stats: {
    count: number;
    successCount: number;
    min: number;
    max: number;
    mean: number;
    median: number;
    p95: number;
    stdDev: number;
    allHashesMatch: boolean;
  };
  params: {
    query: string;
    iterations: number;
    timestamp: string;
  };
};

type FormField = {
  key: string;
  type: string;
  description: string;
  required: boolean;
  itemType?: string;
};

function BenchmarkChart({ runs, stats }: { runs: BenchmarkRun[]; stats: BenchmarkResult["stats"] }) {
  const successRuns = runs.filter((r) => !r.error);
  if (successRuns.length === 0) return null;

  const svgWidth = 600;
  const svgHeight = 220;
  const padLeft = 52;
  const padRight = 16;
  const padTop = 16;
  const padBottom = 32;
  const chartW = svgWidth - padLeft - padRight;
  const chartH = svgHeight - padTop - padBottom;

  const maxTiming = Math.max(...successRuns.map((r) => r.timing), 1);
  const gridStep = Math.max(250, Math.ceil(maxTiming / 4 / 250) * 250);
  const gridMax = Math.ceil(maxTiming / gridStep) * gridStep;
  const gridCount = Math.round(gridMax / gridStep);
  const yScale = (v: number) => padTop + chartH - (v / gridMax) * chartH;
  const barGap = 2;
  const barWidth = Math.max(4, Math.min(40, (chartW - barGap * successRuns.length) / successRuns.length));
  const totalBarsWidth = successRuns.length * (barWidth + barGap) - barGap;
  const offsetX = padLeft + (chartW - totalBarsWidth) / 2;

  const barColor = (timing: number) => TIMING_HEX[timingColor(timing)];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <svg width="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ display: "block" }}>
        {/* Grid lines and y-axis labels */}
        {Array.from({ length: gridCount + 1 }, (_, i) => {
          const val = gridStep * i;
          const y = yScale(val);
          return (
            <g key={i}>
              <line x1={padLeft} x2={svgWidth - padRight} y1={y} y2={y}
                stroke="#e0e0e0" strokeWidth={1} />
              <text x={padLeft - 6} y={y + 4} textAnchor="end"
                fontSize={10} fill="#9e9e9e" fontFamily="monospace">
                {fmtMs(val)}
              </text>
            </g>
          );
        })}

        {/* Threshold lines: green ceiling (1000ms) and yellow ceiling (3000ms) */}
        {maxTiming > 1000 && (
          <>
            <line x1={padLeft} x2={svgWidth - padRight}
              y1={yScale(1000)} y2={yScale(1000)}
              stroke={TIMING_HEX.success} strokeWidth={1} strokeDasharray="3,4" opacity={0.45} />
            <text x={padLeft + 4} y={yScale(1000) - 3}
              fontSize={8} fill={TIMING_HEX.success} fontFamily="monospace" opacity={0.7}>
              1,000ms
            </text>
          </>
        )}
        {maxTiming > 3000 && (
          <>
            <line x1={padLeft} x2={svgWidth - padRight}
              y1={yScale(3000)} y2={yScale(3000)}
              stroke={TIMING_HEX.warning} strokeWidth={1} strokeDasharray="3,4" opacity={0.45} />
            <text x={padLeft + 4} y={yScale(3000) - 3}
              fontSize={8} fill={TIMING_HEX.warning} fontFamily="monospace" opacity={0.7}>
              3,000ms
            </text>
          </>
        )}

        {/* Median line */}
        <line
          x1={padLeft} x2={svgWidth - padRight}
          y1={yScale(stats.median)} y2={yScale(stats.median)}
          stroke={STAT_COLORS.median} strokeWidth={1.5} strokeDasharray="6,3"
        />
        <text x={svgWidth - padRight - 50} y={yScale(stats.median) - 4}
          fontSize={9} fill={STAT_COLORS.median} fontFamily="monospace" fontWeight={700}>
          median
        </text>

        {/* Mean line */}
        {stats.mean !== stats.median && (
          <>
            <line
              x1={padLeft} x2={svgWidth - padRight}
              y1={yScale(stats.mean)} y2={yScale(stats.mean)}
              stroke={STAT_COLORS.mean} strokeWidth={1.5} strokeDasharray="4,4"
            />
            <text x={svgWidth - padRight - 50} y={yScale(stats.mean) - 4}
              fontSize={9} fill={STAT_COLORS.mean} fontFamily="monospace" fontWeight={700}>
              mean
            </text>
          </>
        )}

        {/* P95 line */}
        {stats.p95 !== stats.median && (
          <>
            <line
              x1={padLeft} x2={svgWidth - padRight}
              y1={yScale(stats.p95)} y2={yScale(stats.p95)}
              stroke={STAT_COLORS.p95} strokeWidth={1.5} strokeDasharray="3,3"
            />
            <text x={svgWidth - padRight - 50} y={yScale(stats.p95) - 4}
              fontSize={9} fill={STAT_COLORS.p95} fontFamily="monospace" fontWeight={700}>
              p95
            </text>
          </>
        )}

        {/* Bars */}
        {successRuns.map((run, i) => {
          const x = offsetX + i * (barWidth + barGap);
          const barH = (run.timing / gridMax) * chartH;
          return (
            <g key={run.iteration}>
              <rect
                x={x} y={yScale(run.timing)}
                width={barWidth} height={barH}
                rx={2} fill={barColor(run.timing)}
                opacity={0.85}
              >
                <title>#{run.iteration}: {fmtMs(run.timing)}ms</title>
              </rect>
              <text
                x={x + barWidth / 2} y={svgHeight - padBottom + 14}
                textAnchor="middle" fontSize={9} fill="#757575" fontFamily="monospace"
              >
                {run.iteration}
              </text>
            </g>
          );
        })}

        {/* Axes */}
        <line x1={padLeft} x2={padLeft} y1={padTop} y2={svgHeight - padBottom}
          stroke="#bdbdbd" strokeWidth={1} />
        <line x1={padLeft} x2={svgWidth - padRight} y1={svgHeight - padBottom} y2={svgHeight - padBottom}
          stroke="#bdbdbd" strokeWidth={1} />

        {/* Axis labels */}
        <text x={12} y={svgHeight / 2} textAnchor="middle" fontSize={10} fill="#9e9e9e"
          fontFamily="monospace" transform={`rotate(-90,12,${svgHeight / 2})`}>
          ms
        </text>
        <text x={padLeft + chartW / 2} y={svgHeight - 2} textAnchor="middle"
          fontSize={10} fill="#9e9e9e" fontFamily="monospace">
          Iteration
        </text>
      </svg>
    </Paper>
  );
}

async function getCognitoJWT(): Promise<string> {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

function getApiBase(): string {
  return (
    process.env.REACT_APP_API_BASE_URL ||
    `http://${window.location.hostname}:3001`
  );
}

function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return Math.abs(hash).toString(16).padStart(8, "0");
}

function computeStats(timings: number[]) {
  if (timings.length === 0)
    return { min: 0, max: 0, mean: 0, median: 0, p95: 0, stdDev: 0 };
  const sorted = [...timings].sort((a, b) => a - b);
  const sum = sorted.reduce((a, b) => a + b, 0);
  const mean = sum / sorted.length;
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];
  const p95Index = Math.min(
    Math.ceil(sorted.length * 0.95) - 1,
    sorted.length - 1
  );
  const variance =
    sorted.reduce((acc, v) => acc + (v - mean) ** 2, 0) / sorted.length;
  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    mean: Math.round(mean),
    median: Math.round(median),
    p95: sorted[p95Index],
    stdDev: Math.round(Math.sqrt(variance))
  };
}

function buildFormFieldsFromSchema(schema: any): FormField[] {
  if (!schema?.properties) return [];
  const required = new Set(schema.required || []);
  return Object.entries(schema.properties).map(([key, prop]: [string, any]) => {
    let type = prop.type || "string";
    let itemType: string | undefined;
    if (type === "array") {
      itemType = prop.items?.type || "string";
    }
    return {
      key,
      type,
      description: prop.description || key,
      required: required.has(key),
      itemType,
    };
  });
}

function isUserIdField(key: string): boolean {
  return key === "userId" || key.endsWith("UserId");
}

function coerceValue(value: string, type: string, itemType?: string): any {
  if (type === "boolean") return value === "true";
  if (type === "integer" || type === "number") return Number(value);
  if (type === "array") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch { /* fall through to split */ }
    return value.split(",").map((v) => v.trim()).filter(Boolean).map((v) => {
      if (itemType === "number" || itemType === "integer") return Number(v);
      return v;
    });
  }
  return value;
}

function fmtMs(v: number): string {
  return v.toLocaleString();
}

function timingColor(ms: number): "success" | "warning" | "error" {
  return ms < 1000 ? "success" : ms < 3000 ? "warning" : "error";
}

const TIMING_HEX: Record<string, string> = {
  success: "#4caf50",
  warning: "#ff9800",
  error: "#f44336",
};

const STAT_COLORS = {
  median: "#1565c0",
  mean: "#7b1fa2",
  min: "#2e7d32",
  max: "#d32f2f",
  p95: "#e65100",
  stdDev: "#616161",
};

function downloadCSV(columns: string[], rows: Record<string, any>[]) {
  const csvEscape = (v: string) =>
    v.includes(",") || v.includes('"') || v.includes("\n")
      ? `"${v.replace(/"/g, '""')}"`
      : v;
  const header = columns.map(csvEscape).join(",");
  const csvRows = rows.map((row) =>
    columns.map((col) => csvEscape(String(row[col] ?? ""))).join(",")
  );
  const csv = [header, ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `query-results-${new Date().toISOString().slice(0, 19)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const QueryExplorer = () => {
  const [searchParams] = useSearchParams();
  const presetQueryId = searchParams.get("query");

  // Query selection
  const [queries, setQueries] = useState<RegisteredQuery[]>([]);
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<RegisteredQuery | null>(null);
  const [useResolver, setUseResolver] = useState(true);

  // Entity pickers
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<ProgramOption | null>(null);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Form state: raw string values for non-entity fields, userId values for entity fields
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [userSelections, setUserSelections] = useState<Record<string, MemberOption | null>>({});

  // Results
  const [loading, setLoading] = useState(false);
  const [queryPlanLoading, setQueryPlanLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [history, setHistory] = useState<RunResult[]>([]);

  // Query Plan
  const [queryPlan, setQueryPlan] = useState<PlanRow[] | null>(null);
  const [explainRows, setExplainRows] = useState<ExplainRow[]>([]);
  const [queryPlanSql, setQueryPlanSql] = useState("");

  // Benchmark
  const [benchmarkIterations, setBenchmarkIterations] = useState(5);
  const [benchmarking, setBenchmarking] = useState(false);
  const [benchmarkProgress, setBenchmarkProgress] = useState(0);
  const [benchmarkResult, setBenchmarkResult] = useState<BenchmarkResult | null>(null);
  const benchmarkAbortRef = useRef(false);

  // UI tabs
  const [rightTab, setRightTab] = useState(0);
  const [resultsSubTab, setResultsSubTab] = useState(0);

  // Live timer
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerStartRef = useRef<number>(0);
  const timerActive = loading || benchmarking || queryPlanLoading;

  useEffect(() => {
    if (!timerActive) return;
    timerStartRef.current = performance.now();
    setElapsedMs(0);
    const interval = setInterval(() => {
      setElapsedMs(Math.round(performance.now() - timerStartRef.current));
    }, 53);
    return () => clearInterval(interval);
  }, [timerActive]);

  // Load queries on mount
  useEffect(() => {
    (async () => {
      setQueriesLoading(true);
      try {
        const token = await getCognitoJWT();
        const resp = await fetch(`${getApiBase()}/api/debug/queries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setQueries(data.queries || []);
        if (presetQueryId && data.queries) {
          const preset = data.queries.find((q: RegisteredQuery) => q.id === presetQueryId);
          if (preset) setSelectedQuery(preset);
        }
      } catch (err: any) {
        setError(`Failed to load queries: ${err.message}`);
      } finally {
        setQueriesLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived: current schema and form fields
  const currentSchema = selectedQuery
    ? useResolver && selectedQuery.hasResolver && selectedQuery.resolverSchema
      ? selectedQuery.resolverSchema
      : selectedQuery.schema
    : null;
  const formFields = currentSchema ? buildFormFieldsFromSchema(currentSchema) : [];
  const hasUserIdFields = formFields.some((f) => f.type === "string" && isUserIdField(f.key));

  // Reset form when query or resolver mode changes
  useEffect(() => {
    setFormValues({});
    setUserSelections({});
    setError(null);
    setSelectedProgram(null);
    setMembers([]);
  }, [selectedQuery, useResolver]);

  // Load programs
  const loadPrograms = useCallback(async () => {
    if (programs.length > 0) return;
    setProgramsLoading(true);
    try {
      const resp = await API.get("simpl-api", "programs", {
        queryStringParameters: { limit: "500" }
      });
      const programList = (resp.programs || [])
        .map((p: any) => ({
          id: p.id,
          name: p.name || "Unnamed",
          institutionName: p.institution?.name
        }))
        .sort((a: ProgramOption, b: ProgramOption) =>
          a.name.localeCompare(b.name)
        );
      setPrograms(programList);
    } catch (err) {
      console.error("Failed to load programs:", err);
    } finally {
      setProgramsLoading(false);
    }
  }, [programs.length]);

  // Load members when a program is selected
  const loadMembers = useCallback(async (programId: string) => {
    setMembersLoading(true);
    setMembers([]);
    setUserSelections({});
    try {
      const resp = await API.get("simpl-api", "program-memberships/search", {
        queryStringParameters: {
          filter: JSON.stringify({
            programId: { eq: programId },
            endDate: { activeOnly: true }
          })
        }
      });
      const memberList = (resp.programmemberships || []).map((m: any) => ({
        userId: m.programMember?.id || m.programMemberId,
        fullName:
          `${m.programMember?.firstName || ""} ${m.programMember?.lastName || ""}`.trim() ||
          "Unknown",
        email: m.programMember?.email || "",
        roleName: m.programRole || "Unknown",
        membershipId: m.id
      }));
      memberList.sort((a: MemberOption, b: MemberOption) => {
        const roleOrder: Record<string, number> = { Trainee: 0, Attending: 1 };
        const ra = roleOrder[a.roleName] ?? 2;
        const rb = roleOrder[b.roleName] ?? 2;
        if (ra !== rb) return ra - rb;
        return a.fullName.localeCompare(b.fullName);
      });
      setMembers(memberList);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  // Build params from form
  const buildParams = useCallback(() => {
    const params: Record<string, any> = {};
    for (const field of formFields) {
      if (isUserIdField(field.key) && field.type === "string") {
        const sel = userSelections[field.key];
        if (sel) {
          params[field.key] = sel.userId;
        } else if (formValues[field.key]) {
          params[field.key] = formValues[field.key];
        } else if (!field.required) {
          continue;
        }
      } else {
        const raw = formValues[field.key];
        if (raw === undefined || raw === "") {
          if (!field.required) continue;
        }
        params[field.key] = coerceValue(raw || "", field.type, field.itemType);
      }
    }
    return params;
  }, [formFields, formValues, userSelections]);

  // Execute a single query and return structured result
  const executeSingleQuery = useCallback(
    async (token: string): Promise<RunResult> => {
      if (!selectedQuery) throw new Error("No query selected");
      const body = {
        queryId: selectedQuery.id,
        params: buildParams(),
        mode: "query",
        useResolver: useResolver && selectedQuery.hasResolver,
      };

      const startTime = performance.now();
      const resp = await fetch(`${getApiBase()}/api/debug/queries`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const clientTiming = Math.round(performance.now() - startTime);

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);

      const rows = data.rows || [];
      const stableJson = JSON.stringify(rows);
      const hash = hashString(stableJson);

      return {
        columns: data.columns || [],
        rows,
        timing: clientTiming,
        rowCount: data.rowCount || rows.length,
        hash,
        sql: data.sql || "",
        timestamp: new Date().toISOString(),
      };
    },
    [selectedQuery, buildParams, useResolver]
  );

  // Run Query
  const runQuery = useCallback(async () => {
    if (!selectedQuery) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getCognitoJWT();
      const runResult = await executeSingleQuery(token);
      setResult(runResult);
      setHistory((prev) => [runResult, ...prev].slice(0, 20));
      setRightTab(1);
      setResultsSubTab(0);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [selectedQuery, executeSingleQuery]);

  // Run Explain
  const runExplain = useCallback(async () => {
    if (!selectedQuery) return;
    setQueryPlanLoading(true);
    setError(null);
    try {
      const token = await getCognitoJWT();
      const body = {
        queryId: selectedQuery.id,
        params: buildParams(),
        mode: "explain",
        useResolver: useResolver && selectedQuery.hasResolver,
      };
      const resp = await fetch(`${getApiBase()}/api/debug/queries`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setQueryPlan(data.queryPlan || []);
      setExplainRows(data.explain || []);
      setQueryPlanSql(data.sql || "");
      setRightTab(0);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setQueryPlanLoading(false);
    }
  }, [selectedQuery, buildParams, useResolver]);

  // Run Benchmark (client-side, with abort and progress)
  const runBenchmark = useCallback(async () => {
    if (!selectedQuery) return;
    setBenchmarking(true);
    setBenchmarkProgress(0);
    setBenchmarkResult(null);
    benchmarkAbortRef.current = false;
    setError(null);

    const runs: BenchmarkRun[] = [];
    try {
      const token = await getCognitoJWT();
      for (let i = 0; i < benchmarkIterations; i++) {
        if (benchmarkAbortRef.current) break;
        setBenchmarkProgress(i + 1);
        try {
          const run = await executeSingleQuery(token);
          runs.push({
            iteration: i + 1,
            timing: run.timing,
            rowCount: run.rowCount,
            hash: run.hash,
          });
        } catch (err: any) {
          runs.push({
            iteration: i + 1,
            timing: 0,
            rowCount: 0,
            hash: "error",
            error: err.message || String(err),
          });
        }
      }

      const successRuns = runs.filter((r) => !r.error);
      const timings = successRuns.map((r) => r.timing);
      const hashes = new Set(successRuns.map((r) => r.hash));
      const stats = computeStats(timings);

      setBenchmarkResult({
        runs,
        stats: {
          count: runs.length,
          successCount: successRuns.length,
          ...stats,
          allHashesMatch: hashes.size <= 1,
        },
        params: {
          query: selectedQuery.name,
          iterations: benchmarkIterations,
          timestamp: new Date().toISOString(),
        },
      });
      setRightTab(2);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setBenchmarking(false);
    }
  }, [selectedQuery, benchmarkIterations, executeSingleQuery]);

  const exportBenchmark = useCallback(() => {
    if (!benchmarkResult) return;
    const blob = new Blob([JSON.stringify(benchmarkResult, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = benchmarkResult.params.timestamp.replace(/[:.]/g, "-").slice(0, 19);
    a.download = `benchmark-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [benchmarkResult]);

  const copyResultJson = useCallback(() => {
    if (result) {
      navigator.clipboard.writeText(JSON.stringify(result.rows, null, 2));
    }
  }, [result]);

  const canExecute = selectedQuery != null;
  const anyActive = loading || benchmarking || queryPlanLoading;

  const formatElapsed = (ms: number) =>
    ms < 1000
      ? `${ms}ms`
      : ms < 10000
        ? `${(ms / 1000).toFixed(1)}s`
        : `${Math.round(ms / 1000)}s`;

  const elapsedColor = elapsedMs < 3000 ? "#1565c0" : elapsedMs < 10000 ? "#e65100" : "#b71c1c";
  const elapsedBg = elapsedMs < 3000 ? "#e3f2fd" : elapsedMs < 10000 ? "#fff3e0" : "#ffebee";

  return (
    <PageWrapper
      className="QueryExplorer"
      headerText="Query Explorer"
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {/* ── Left Column: Controls ── */}
        <Card
          sx={{
            width: 380,
            minWidth: 380,
            position: "sticky",
            top: 16,
            mt: 2,
            maxHeight: "calc(100vh - 100px)",
            overflowY: "auto"
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              {/* Query Selector */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -0.5 }}>
                Query
              </Typography>
              <Autocomplete
                size="small"
                options={queries}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => option.name}
                value={selectedQuery}
                loading={queriesLoading}
                onChange={(_, val) => {
                  setSelectedQuery(val);
                  setResult(null);
                  setHistory([]);
                  setQueryPlan(null);
                  setBenchmarkResult(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Query"
                    placeholder="Choose a query..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {queriesLoading && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Stack>
                  </li>
                )}
              />

              {selectedQuery && (
                <Alert severity="info" sx={{ py: 0.5, fontSize: "0.8rem" }}>
                  {selectedQuery.description}
                </Alert>
              )}

              {/* Resolver Toggle */}
              {selectedQuery?.hasResolver && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={useResolver}
                      onChange={(e) => setUseResolver(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Use resolver (simplified inputs)
                    </Typography>
                  }
                  sx={{ ml: 0 }}
                />
              )}

              {/* Parameters */}
              {selectedQuery && formFields.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: -0.5 }}>
                    Parameters
                  </Typography>

                  {/* Program→User picker when any userId fields exist */}
                  {hasUserIdFields && (
                    <Autocomplete
                      size="small"
                      options={programs}
                      loading={programsLoading}
                      getOptionLabel={(opt) =>
                        opt.institutionName
                          ? `${opt.name} (${opt.institutionName})`
                          : opt.name
                      }
                      value={selectedProgram}
                      onOpen={loadPrograms}
                      onChange={(_, val) => {
                        setSelectedProgram(val);
                        setResult(null);
                        setUserSelections({});
                        if (val) {
                          loadMembers(val.id);
                        } else {
                          setMembers([]);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Program (to find users)"
                          placeholder="Search programs..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {programsLoading && <CircularProgress size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  )}

                  {formFields.map((field) => {
                    // Boolean fields → Switch
                    if (field.type === "boolean") {
                      return (
                        <FormControlLabel
                          key={field.key}
                          control={
                            <Switch
                              checked={formValues[field.key] === "true"}
                              onChange={(e) =>
                                setFormValues((prev) => ({
                                  ...prev,
                                  [field.key]: e.target.checked ? "true" : "false",
                                }))
                              }
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {field.description}
                              {field.required && (
                                <Typography component="span" color="error" sx={{ ml: 0.3 }}>*</Typography>
                              )}
                            </Typography>
                          }
                          sx={{ ml: 0 }}
                        />
                      );
                    }

                    // UserId fields → Autocomplete over loaded members
                    if (field.type === "string" && isUserIdField(field.key)) {
                      return (
                        <Box key={field.key}>
                          <Autocomplete
                            size="small"
                            options={members}
                            loading={membersLoading}
                            disabled={!selectedProgram}
                            groupBy={(option) => option.roleName}
                            getOptionLabel={(opt) =>
                              `${opt.fullName} (${opt.roleName})`
                            }
                            value={userSelections[field.key] || null}
                            onChange={(_, val) => {
                              setUserSelections((prev) => ({ ...prev, [field.key]: val }));
                              if (val) {
                                setFormValues((prev) => ({ ...prev, [field.key]: val.userId }));
                              } else {
                                setFormValues((prev) => {
                                  const next = { ...prev };
                                  delete next[field.key];
                                  return next;
                                });
                              }
                              setResult(null);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={field.description}
                                placeholder={selectedProgram ? "Search members..." : "Select a program first..."}
                                required={field.required}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {membersLoading && <CircularProgress size={20} />}
                                      {params.InputProps.endAdornment}
                                    </>
                                  )
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={`${field.key}-${option.membershipId}`}>
                                <Stack>
                                  <Typography variant="body2">
                                    {option.fullName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.email} &middot;{" "}
                                    <Chip
                                      label={option.roleName}
                                      size="small"
                                      color={option.roleName === "Trainee" ? "primary" : "secondary"}
                                      sx={{ height: 18, fontSize: "0.7rem" }}
                                    />
                                  </Typography>
                                </Stack>
                              </li>
                            )}
                          />
                          {userSelections[field.key] && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, ml: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                {userSelections[field.key]!.userId}
                              </Typography>
                              <Chip
                                label={userSelections[field.key]!.roleName}
                                color={userSelections[field.key]!.roleName === "Trainee" ? "primary" : "secondary"}
                                size="small"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                            </Stack>
                          )}
                        </Box>
                      );
                    }

                    // Default: TextField
                    return (
                      <TextField
                        key={field.key}
                        label={field.description}
                        value={formValues[field.key] || ""}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        size="small"
                        required={field.required}
                        helperText={
                          field.type === "array"
                            ? "Comma-separated or JSON array"
                            : undefined
                        }
                        placeholder={
                          field.type === "array"
                            ? '["id1","id2"] or id1,id2'
                            : undefined
                        }
                      />
                    );
                  })}
                </>
              )}

              {/* Action Buttons */}
              {selectedQuery && (
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
                  <Stack spacing={1.5}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={
                        queryPlanLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <AccountTreeIcon />
                        )
                      }
                      onClick={runExplain}
                      disabled={anyActive || !canExecute}
                    >
                      {queryPlanLoading ? "Explaining..." : "Explain"}
                    </Button>

                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={
                        loading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PlayArrowIcon />
                        )
                      }
                      onClick={runQuery}
                      disabled={anyActive || !canExecute}
                    >
                      {loading ? (
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                          Running...
                          <Chip
                            label={formatElapsed(elapsedMs)}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontFamily: "monospace",
                              fontWeight: "bold",
                              backgroundColor: elapsedBg,
                              color: elapsedColor,
                            }}
                          />
                        </Box>
                      ) : "Run Query"}
                    </Button>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {benchmarking ? (
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          startIcon={<StopIcon />}
                          onClick={() => { benchmarkAbortRef.current = true; }}
                        >
                          Stop ({benchmarkProgress}/{benchmarkIterations})
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<SpeedIcon />}
                          onClick={runBenchmark}
                          disabled={loading || queryPlanLoading || !canExecute}
                        >
                          Benchmark
                        </Button>
                      )}
                      <TextField
                        type="number"
                        size="small"
                        value={benchmarkIterations}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (v > 0 && v <= 50) setBenchmarkIterations(v);
                        }}
                        disabled={benchmarking}
                        inputProps={{ min: 1, max: 50, style: { width: 40 } }}
                        sx={{ width: 80, minWidth: 80 }}
                        label="Runs"
                      />
                    </Stack>

                    {benchmarking && (
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={(benchmarkProgress / benchmarkIterations) * 100}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Iteration {benchmarkProgress} of {benchmarkIterations}
                          </Typography>
                          <Chip
                            label={formatElapsed(elapsedMs)}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              fontFamily: "monospace",
                              fontWeight: "bold",
                              backgroundColor: elapsedBg,
                              color: elapsedColor,
                            }}
                          />
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* ── Right Column: Tabbed panels ── */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Tabs
            value={rightTab}
            onChange={(_, v) => setRightTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}
          >
            <Tab
              icon={<AccountTreeIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Query Plan"
              sx={{ textTransform: "none", minHeight: 42 }}
            />
            <Tab
              icon={<TableChartIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={result ? `Results (${result.rowCount})` : "Results"}
              sx={{ textTransform: "none", minHeight: 42 }}
            />
            <Tab
              icon={<SpeedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Benchmark"
              sx={{ textTransform: "none", minHeight: 42 }}
            />
          </Tabs>

          {/* ── Query Plan tab ── */}
          {rightTab === 0 && (
            <Box sx={{ pt: 2 }}>
              {queryPlan ? (
                <QueryPlanExplorer plan={queryPlan} explainRows={explainRows} sql={queryPlanSql} />
              ) : (
                <Alert severity="info">
                  Click "Explain" to generate the query plan.
                </Alert>
              )}
            </Box>
          )}

          {/* ── Results tab ── */}
          {rightTab === 1 && (
            <Box sx={{ pt: 0 }}>
              {result ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                    <Chip
                      label={`${fmtMs(result.timing)}ms`}
                      color={timingColor(result.timing)}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={result.hash}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {result.rowCount} rows
                    </Typography>
                  </Stack>

                  <Tabs
                    value={resultsSubTab}
                    onChange={(_, v) => setResultsSubTab(v)}
                    sx={{ minHeight: 32, borderBottom: 1, borderColor: "divider", mb: 1.5 }}
                  >
                    <Tab label="Table" sx={{ minHeight: 32, textTransform: "none", fontSize: "0.8rem" }} />
                    <Tab label="Raw JSON" sx={{ minHeight: 32, textTransform: "none", fontSize: "0.8rem" }} />
                    {history.length > 1 && (
                      <Tab label={`Run History (${history.length})`} sx={{ minHeight: 32, textTransform: "none", fontSize: "0.8rem" }} />
                    )}
                  </Tabs>

                  {/* Table sub-tab */}
                  {resultsSubTab === 0 && result.columns.length > 0 && (
                    <Box sx={{ position: "relative" }}>
                      <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 4, right: 20, zIndex: 3 }}>
                        <Tooltip title="Copy as CSV">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const header = result.columns.join(",");
                              const rows = result.rows.map((row) =>
                                result.columns.map((col) => {
                                  const val = String(row[col] ?? "");
                                  return val.includes(",") || val.includes('"')
                                    ? `"${val.replace(/"/g, '""')}"`
                                    : val;
                                }).join(",")
                              );
                              navigator.clipboard.writeText([header, ...rows].join("\n"));
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download CSV">
                          <IconButton
                            size="small"
                            onClick={() => downloadCSV(result.columns, result.rows)}
                          >
                            <DownloadIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <TableContainer component={Paper} sx={{ maxHeight: "65vh" }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              {result.columns.map((col) => (
                                <TableCell
                                  key={col}
                                  sx={{ fontWeight: 700, fontSize: "0.75rem", whiteSpace: "nowrap" }}
                                >
                                  {col}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {result.rows.slice(0, 500).map((row, idx) => (
                              <TableRow key={idx} hover>
                                {result.columns.map((col) => (
                                  <TableCell
                                    key={col}
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontFamily: "monospace",
                                      maxWidth: 300,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      py: 0.5,
                                    }}
                                  >
                                    {row[col] === null ? (
                                      <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                        null
                                      </Typography>
                                    ) : (
                                      String(row[col])
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {result.rows.length > 500 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Showing first 500 of {result.rows.length} rows
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Raw JSON sub-tab */}
                  {resultsSubTab === 1 && (
                    <Box sx={{ position: "relative" }}>
                      <Tooltip title="Copy JSON">
                        <IconButton
                          size="small"
                          onClick={copyResultJson}
                          sx={{ position: "absolute", top: 4, right: 20, zIndex: 1, color: "#999" }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Box
                        component="pre"
                        sx={{
                          overflow: "auto",
                          maxHeight: "65vh",
                          fontSize: "0.75rem",
                          backgroundColor: "#f5f5f5",
                          p: 2,
                          borderRadius: 1
                        }}
                      >
                        {JSON.stringify(result.rows, null, 2)}
                      </Box>
                    </Box>
                  )}

                  {/* Run History sub-tab */}
                  {resultsSubTab === 2 && history.length > 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Matching hashes confirm identical result sets across runs.
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Time</TableCell>
                              <TableCell>Response (ms)</TableCell>
                              <TableCell>Rows</TableCell>
                              <TableCell>Hash</TableCell>
                              <TableCell>Match?</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {history.map((run, idx) => (
                              <TableRow key={run.timestamp}>
                                <TableCell>{history.length - idx}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {new Date(run.timestamp).toLocaleTimeString()}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${fmtMs(run.timing)}ms`}
                                    size="small"
                                    color={timingColor(run.timing)}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{fmtMs(run.rowCount)}</TableCell>
                                <TableCell>
                                  <code>{run.hash}</code>
                                </TableCell>
                                <TableCell>
                                  {idx < history.length - 1 ? (
                                    run.hash === history[history.length - 1].hash ? (
                                      <Chip label="Match" size="small" color="success" />
                                    ) : (
                                      <Chip label="Different" size="small" color="error" />
                                    )
                                  ) : (
                                    <Chip label="Baseline" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              ) : (
                <Box sx={{ pt: 2 }}>
                  <Alert severity="info">
                    Run a query to see results.
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {/* ── Benchmark tab ── */}
          {rightTab === 2 && (
            <Box sx={{ pt: 2 }}>
              {benchmarkResult ? (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="h6">Benchmark Results</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={exportBenchmark}
                      >
                        Export JSON
                      </Button>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {benchmarkResult.params.query} &mdash;{" "}
                      {new Date(benchmarkResult.params.timestamp).toLocaleString()}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                      <Chip label={`Median: ${fmtMs(benchmarkResult.stats.median)}ms`}
                        sx={{ backgroundColor: STAT_COLORS.median, color: "#fff", fontWeight: 700 }} />
                      <Chip label={`Mean: ${fmtMs(benchmarkResult.stats.mean)}ms`}
                        variant="outlined" sx={{ borderColor: STAT_COLORS.mean, color: STAT_COLORS.mean, fontWeight: 600 }} />
                      <Chip label={`Min: ${fmtMs(benchmarkResult.stats.min)}ms`}
                        variant="outlined" sx={{ borderColor: STAT_COLORS.min, color: STAT_COLORS.min }} />
                      <Chip label={`Max: ${fmtMs(benchmarkResult.stats.max)}ms`}
                        variant="outlined" sx={{ borderColor: STAT_COLORS.max, color: STAT_COLORS.max }} />
                      <Chip label={`P95: ${fmtMs(benchmarkResult.stats.p95)}ms`}
                        variant="outlined" sx={{ borderColor: STAT_COLORS.p95, color: STAT_COLORS.p95 }} />
                      <Chip label={`StdDev: ${fmtMs(benchmarkResult.stats.stdDev)}ms`}
                        variant="outlined" sx={{ borderColor: STAT_COLORS.stdDev, color: STAT_COLORS.stdDev }} />
                      <Chip
                        label={`${benchmarkResult.stats.successCount}/${benchmarkResult.stats.count} succeeded`}
                        color={benchmarkResult.stats.successCount === benchmarkResult.stats.count ? "success" : "warning"}
                        variant="outlined"
                      />
                      <Chip
                        label={benchmarkResult.stats.allHashesMatch ? "All hashes match" : "Hash mismatch!"}
                        color={benchmarkResult.stats.allHashesMatch ? "success" : "error"}
                        variant={benchmarkResult.stats.allHashesMatch ? "outlined" : "filled"}
                      />
                    </Stack>

                    <BenchmarkChart runs={benchmarkResult.runs} stats={benchmarkResult.stats} />

                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Time (ms)</TableCell>
                            <TableCell>Rows</TableCell>
                            <TableCell>Hash</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {benchmarkResult.runs.map((run) => (
                            <TableRow
                              key={run.iteration}
                              sx={run.error ? { backgroundColor: "error.light", opacity: 0.7 } : undefined}
                            >
                              <TableCell>{run.iteration}</TableCell>
                              <TableCell>
                                {run.error ? "—" : (
                                  <Chip
                                    label={`${fmtMs(run.timing)}ms`}
                                    size="small"
                                    color={timingColor(run.timing)}
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell>{run.error ? "—" : fmtMs(run.rowCount)}</TableCell>
                              <TableCell><code>{run.hash}</code></TableCell>
                              <TableCell>
                                {run.error ? (
                                  <Tooltip title={run.error}>
                                    <Chip label="Error" size="small" color="error" />
                                  </Tooltip>
                                ) : (
                                  <Chip label="OK" size="small" color="success" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="info">
                  Run a benchmark to see results.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </PageWrapper>
  );
};
